import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Paper, Tabs, Tab, Typography, Alert, Button,
  Table, TableHead, TableBody, TableRow, TableCell,
  IconButton, Collapse, CircularProgress, TextField,
  Chip, Checkbox, Snackbar,
} from '@mui/material';
import {
  KeyboardArrowDown as DownIcon,
  KeyboardArrowRight as RightIcon,
  Refresh as RefreshIcon,
  ArrowBack as ReturnIcon,
  LocalShipping as ShipIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_ENDPOINTS } from '../config';
import BulkProcessModal from '../components/BulkProcessModal';

// ============================================================
//  Cold Store Out — Phase 2B Session 1.5
//
//  Five-tab bulk-select UI. Each tab filters inventory by its
//  source statuses. Pallet-level checkboxes + editable cartons
//  inline. Click selection bar action → BulkProcessModal.
//
//  Tabs: repack / reprint / reprocess / qc / shipment
// ============================================================

const TABS = {
  repack: {
    id: 'repack', label: 'Repack-Out', color: '#ea580c',
    statuses: ['REPACK-OUT'],                returnTarget: 'REPACK-IN',
    actions: ['return', 'shipment'],         docPrefix: 'RPK',
    description: 'Pallets sent for re-packing',
  },
  reprint: {
    id: 'reprint', label: 'Reprint-Out', color: '#db2777',
    statuses: ['RE-PRINTING'],               returnTarget: 'REPACK-IN',
    actions: ['return', 'shipment'],         docPrefix: 'RPT',
    description: 'Pallets sent for label reprint',
  },
  reprocess: {
    id: 'reprocess', label: 'Reprocess-Out', color: '#9333ea',
    statuses: ['RE-PROCESS'],                returnTarget: 'REPROCESS-IN',
    actions: ['return', 'shipment'],         docPrefix: 'RPS',
    description: 'Pallets sent for re-processing',
  },
  qc: {
    id: 'qc', label: 'QC Sample', color: '#0891b2',
    statuses: ['QC SAMPLE', 'QC-SAMPLE-IN'], returnTarget: 'QC-SAMPLE-IN',
    actions: ['return'],                     docPrefix: 'QC',
    description: 'Pallets sent for QC inspection',
  },
  shipment: {
    id: 'shipment', label: 'Shipment Out', color: '#7c3aed',
    statuses: ['SHIPPED', 'SHIPMENT-PENDING'], returnTarget: null,
    actions: [],                             docPrefix: 'FCL',
    description: 'Pallets pending or completed shipment',
  },
};
const TAB_ORDER = ['repack', 'reprint', 'reprocess', 'qc', 'shipment'];

const todayISO = () => new Date().toISOString().slice(0, 10);
const dateMinusDays = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

const getAuthConfig = () => {
  const token = localStorage.getItem('token');
  return {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    withCredentials: true,
  };
};

const hHead = {
  fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4,
  color: '#475569', textTransform: 'uppercase', whiteSpace: 'nowrap',
};

export default function ColdStoreOutPage() {
  const [activeTabId, setActiveTabId] = useState('repack');
  const activeTab = TABS[activeTabId];

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dateFrom, setDateFrom] = useState(dateMinusDays(7));
  const [dateTo, setDateTo] = useState(todayISO());
  const [search, setSearch] = useState('');

  const [expandedGroups, setExpandedGroups] = useState({});
  const [selection, setSelection] = useState(new Map());
  const [bulkAction, setBulkAction] = useState(null);
  const [snack, setSnack] = useState(null);

  // ============================================================
  //  Fetch — uses repack_out view for repack/reprint/reprocess/qc,
  //          shipment view for shipment tab
  // ============================================================
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const view = activeTab.id === 'shipment' ? 'shipment' : 'repack_out';
      const params = new URLSearchParams({
        view,
        date_from: dateFrom,
        date_to: dateTo,
      });
      const url = `${API_ENDPOINTS.INVENTORY}?${params.toString()}`;
      const res = await axios.get(url, getAuthConfig());
      const allRows = res.data?.data || res.data?.rows || [];
      const filtered = allRows.filter(r => activeTab.statuses.includes(r.status));
      setRows(filtered);
    } catch (err) {
      console.error('CS Out fetch error', err);
      setError('Failed to load: ' + (err.response?.data?.error || err.message));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Reset selection on tab change
  useEffect(() => { setSelection(new Map()); }, [activeTabId]);

  // ============================================================
  //  Selection helpers
  // ============================================================
  const togglePallet = (pallet) => setSelection(prev => {
    const n = new Map(prev);
    if (n.has(pallet.id)) n.delete(pallet.id);
    else n.set(pallet.id, { cartons: parseInt(pallet.cartons, 10) || 0 });
    return n;
  });

  const setPalletCartons = (palletId, qty) => setSelection(prev => {
    const n = new Map(prev);
    if (!n.has(palletId)) return prev;
    const v = parseInt(qty, 10);
    if (!v || v <= 0) { n.delete(palletId); return n; }
    const pallet = rows.find(p => p.id === palletId);
    if (!pallet) return prev;
    n.set(palletId, { cartons: Math.min(v, parseInt(pallet.cartons, 10) || 0) });
    return n;
  });

  const toggleGroup = (groupPallets) => {
    const all = groupPallets.every(p => selection.has(p.id));
    setSelection(prev => {
      const n = new Map(prev);
      if (all) groupPallets.forEach(p => n.delete(p.id));
      else groupPallets.forEach(p => {
        if (!n.has(p.id)) n.set(p.id, { cartons: parseInt(p.cartons, 10) || 0 });
      });
      return n;
    });
  };

  const clearSelection = () => setSelection(new Map());

  // Active pallet IDs across the FETCHED set (for client-side hint;
  // backend does the authoritative uniqueness check)
  const activePalletIds = useMemo(
    () => new Set(rows.map(r => String(r.pallet_id))),
    [rows]
  );

  // ============================================================
  //  Group by gateout_no, then by line key
  // ============================================================
  const grouped = useMemo(() => {
    const filtered = !search.trim() ? rows : rows.filter(r => {
      const s = search.toLowerCase();
      return (r.gateout_no || '').toLowerCase().includes(s) ||
             (r.product || '').toLowerCase().includes(s) ||
             (r.lot_no || '').toLowerCase().includes(s) ||
             String(r.pallet_id || '').toLowerCase().includes(s);
    });

    const groups = new Map();
    filtered.forEach(r => {
      const goNo = r.gateout_no || '— (legacy, no GO No)';
      if (!groups.has(goNo)) {
        groups.set(goNo, {
          gateout_no: goNo,
          gateout_date: r.gateout_date || r.repack_out_date || null,
          destination_dept: r.destination_dept || null,
          pallets: [],
        });
      }
      groups.get(goNo).pallets.push(r);
    });

    return Array.from(groups.values()).sort((a, b) =>
      (b.gateout_date || '').localeCompare(a.gateout_date || '')
    );
  }, [rows, search]);

  const toggleGroupExpand = (goNo) =>
    setExpandedGroups(prev => ({ ...prev, [goNo]: !prev[goNo] }));

  // Selection summary
  const summary = useMemo(() => {
    let pCount = 0, cartons = 0, kgs = 0, splits = 0;
    for (const [id, sel] of selection) {
      const pallet = rows.find(p => p.id === id);
      if (!pallet) continue;
      const origCartons = parseInt(pallet.cartons, 10) || 0;
      const cartonKg = origCartons > 0 ? parseFloat(pallet.total_kgs) / origCartons : 0;
      pCount++;
      cartons += sel.cartons;
      kgs += cartonKg * sel.cartons;
      if (sel.cartons < origCartons) splits++;
    }
    return { pCount, cartons, kgs, lbs: kgs * 2.20, splits };
  }, [selection, rows]);

  // Tab counts (for the strip)
  const tabCount = useMemo(() => rows.length, [rows]);

  // ============================================================
  //  Modal callbacks
  // ============================================================
  const handleModalSuccess = (result) => {
    setBulkAction(null);
    setSnack({
      severity: 'success',
      msg: result.message ||
           `${result.processed_count || 0} pallets returned to CS` +
           (result.split_count ? ` (${result.split_count} split)` : ''),
    });
    setTimeout(() => setSnack(null), 6000);
    clearSelection();
    fetchData();
  };

  // ============================================================
  //  RENDER
  // ============================================================
  return (
    <Box sx={{ p: 2 }}>
      {/* HEADER */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          📦 Cold Store Out
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: '#64748b' }}>
          {activeTab.description}
        </Typography>
        <Box sx={{ ml: 'auto' }}>
          <Button size="small" variant="outlined" startIcon={<RefreshIcon />}
                  onClick={fetchData} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      {/* TABS */}
      <Paper elevation={1} sx={{ mb: 2 }}>
        <Tabs
          value={activeTabId}
          onChange={(_, v) => setActiveTabId(v)}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': { minHeight: 48, fontWeight: 600, textTransform: 'none' },
            '& .Mui-selected': { color: activeTab?.color + ' !important' },
            '& .MuiTabs-indicator': { backgroundColor: activeTab?.color },
          }}
        >
          {TAB_ORDER.map(tid => {
            const t = TABS[tid];
            return (
              <Tab key={tid} value={tid} label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {t.label}
                  {activeTabId === tid && (
                    <Chip size="small" label={tabCount}
                          sx={{ height: 18, fontSize: 11,
                                bgcolor: t.color + '22', color: t.color, fontWeight: 700 }} />
                  )}
                </Box>
              } />
            );
          })}
        </Tabs>
      </Paper>

      {/* SELECTION BAR */}
      {summary.pCount > 0 && activeTab.actions.length > 0 && (
        <Paper sx={{
          p: 1.5, mb: 2,
          background: `linear-gradient(90deg, ${activeTab.color}, #8E44AD)`,
          color: 'white',
          display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
        }}>
          <Box sx={{ bgcolor: 'rgba(255,255,255,0.22)', px: 1.5, py: 0.5, borderRadius: 12, fontSize: 13, fontWeight: 700 }}>
            {summary.pCount} pallets
          </Box>
          <Typography sx={{ fontSize: 12.5 }}>
            <strong>{summary.cartons}</strong> cartons ·{' '}
            <strong>{summary.kgs.toFixed(2)} KG</strong> /{' '}
            <strong>{summary.lbs.toFixed(2)} LB</strong>
            {summary.splits > 0 && (
              <span style={{ marginLeft: 8, opacity: 0.92, fontSize: 11.5 }}>
                · {summary.splits} split{summary.splits === 1 ? '' : 's'} detected
              </span>
            )}
          </Typography>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            {activeTab.actions.includes('return') && (
              <Button size="small"
                      startIcon={<ReturnIcon />}
                      onClick={() => setBulkAction('return')}
                      sx={{ bgcolor: 'white', color: '#1d4ed8', fontWeight: 700,
                            '&:hover': { bgcolor: '#f0f9ff' } }}>
                Return to CS ({activeTab.returnTarget})
              </Button>
            )}
            {activeTab.actions.includes('shipment') && (
              <Button size="small"
                      startIcon={<ShipIcon />}
                      onClick={() => setBulkAction('shipment')}
                      sx={{ bgcolor: 'white', color: '#5b21b6', fontWeight: 700,
                            '&:hover': { bgcolor: '#faf5ff' } }}>
                Direct Shipment
              </Button>
            )}
            <Button size="small" onClick={clearSelection}
                    sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'white' }}>
              Clear
            </Button>
          </Box>
        </Paper>
      )}

      {/* INFO BANNER for shipment tab (read-only) */}
      {activeTab.id === 'shipment' && (
        <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>
          <strong>Read-only view.</strong> Pending shipments wait for email approval.
          Once shipped, the audit chain back to the original RPK doc is preserved via
          the <code>prior_gateout_no</code> column.
        </Alert>
      )}

      {/* FILTER ROW */}
      <Paper sx={{ p: 1.25, mb: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Date From</Typography>
        <TextField type="date" size="small" value={dateFrom}
                   onChange={(e) => setDateFrom(e.target.value)} sx={{ width: 145 }} />
        <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>To</Typography>
        <TextField type="date" size="small" value={dateTo}
                   onChange={(e) => setDateTo(e.target.value)} sx={{ width: 145 }} />
        <TextField
          size="small"
          placeholder="Search GO No, product, lot, pallet..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
        />
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={28} />
          <Typography sx={{ mt: 1.5, fontSize: 13, color: '#64748b' }}>
            Loading {activeTab.label}…
          </Typography>
        </Box>
      )}

      {!loading && grouped.length === 0 && (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 32, mb: 1 }}>📭</Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            No {activeTab.label} records in this date range
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b', mt: 0.5 }}>
            Try widening the date range, or gate items out from Cold Store IN.
          </Typography>
        </Paper>
      )}

      {!loading && grouped.length > 0 && grouped.map(g => (
        <GroupCard
          key={g.gateout_no}
          group={g}
          tab={activeTab}
          expanded={!!expandedGroups[g.gateout_no]}
          onToggleExpand={() => toggleGroupExpand(g.gateout_no)}
          selection={selection}
          onTogglePallet={togglePallet}
          onToggleGroup={() => toggleGroup(g.pallets)}
          onSetCartons={setPalletCartons}
        />
      ))}

      {/* BULK MODAL */}
      {bulkAction && (
        <BulkProcessModal
          open={!!bulkAction}
          action={bulkAction}
          tab={activeTab}
          pallets={rows}
          selection={selection}
          activePalletIds={activePalletIds}
          onClose={() => setBulkAction(null)}
          onSuccess={handleModalSuccess}
        />
      )}

      {/* SUCCESS SNACKBAR */}
      <Snackbar
        open={!!snack}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={6000}
        onClose={() => setSnack(null)}
      >
        {snack && (
          <Alert severity={snack.severity || 'success'} variant="filled" onClose={() => setSnack(null)}>
            {snack.msg}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
}

// ============================================================
//  Group card — one Gate-Out doc with its pallets
// ============================================================
function GroupCard({
  group, tab, expanded, onToggleExpand,
  selection, onTogglePallet, onToggleGroup, onSetCartons,
}) {
  const allIn = group.pallets.every(p => selection.has(p.id));
  const someIn = group.pallets.some(p => selection.has(p.id));
  const groupCartons = group.pallets.reduce((s, p) => s + (parseInt(p.cartons, 10) || 0), 0);
  const groupKg = group.pallets.reduce((s, p) => s + (parseFloat(p.total_kgs) || 0), 0);
  const selectable = tab.actions.length > 0;

  return (
    <Paper sx={{ mb: 1.5, overflow: 'hidden' }}>
      <Box sx={{
        bgcolor: '#fafbfc', borderBottom: '1px solid #e2e8f0',
        px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1.25,
      }}>
        {selectable && (
          <Checkbox
            size="small" sx={{ p: 0 }}
            checked={allIn}
            indeterminate={someIn && !allIn}
            onChange={onToggleGroup}
            onClick={(e) => e.stopPropagation()}
          />
        )}
        <IconButton size="small" sx={{ p: 0 }} onClick={onToggleExpand}>
          {expanded ? <DownIcon fontSize="small" /> : <RightIcon fontSize="small" />}
        </IconButton>
        <Box sx={{
          fontFamily: 'JetBrains Mono, monospace', fontSize: 12.5, fontWeight: 700,
          bgcolor: tab.color + '22', color: tab.color,
          px: 1, py: 0.25, borderRadius: 0.5,
        }}>
          {group.gateout_no}
        </Box>
        <Typography sx={{ fontSize: 12, color: '#475569' }}>
          {group.gateout_date || '—'}{group.destination_dept ? ' · ' + group.destination_dept : ''}
        </Typography>
        <Typography sx={{ ml: 'auto', fontSize: 11.5, color: '#64748b', fontFamily: 'JetBrains Mono, monospace' }}>
          {group.pallets.length} pallets · {groupCartons} cartons · {groupKg.toFixed(2)} KG
        </Typography>
      </Box>

      <Collapse in={expanded || !selectable}>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 2000 }}>
            <TableHead>
              <TableRow>
                {selectable && <TableCell sx={{ width: 30 }} />}
                <TableCell sx={hHead}>Pallet ID</TableCell>
                <TableCell sx={hHead}>Prod Date</TableCell>
                <TableCell sx={hHead}>Shift</TableCell>
                <TableCell sx={hHead}>Product</TableCell>
                <TableCell sx={hHead}>Grade</TableCell>
                <TableCell sx={hHead}>Pack Style</TableCell>
                <TableCell sx={hHead}>Pack Size</TableCell>
                <TableCell sx={hHead}>Brand</TableCell>
                <TableCell sx={hHead}>NAV ID</TableCell>
                <TableCell sx={hHead}>Product ID</TableCell>
                <TableCell sx={hHead}>Lot No</TableCell>
                <TableCell sx={hHead}>Day Code</TableCell>
                <TableCell sx={hHead}>Trace ID</TableCell>
                <TableCell sx={hHead}>BIN</TableCell>
                <TableCell sx={hHead} align="right">
                  {selectable ? 'Take Cartons' : 'Cartons'}
                </TableCell>
                <TableCell sx={hHead} align="right">WT/Carton (KG)</TableCell>
                <TableCell sx={hHead} align="right">WT/Carton (LB)</TableCell>
                <TableCell sx={hHead} align="right">Total KG</TableCell>
                <TableCell sx={hHead} align="right">Total LB</TableCell>
                <TableCell sx={hHead}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {group.pallets.map(p => {
                const ticked = selection.has(p.id);
                const origCartons = parseInt(p.cartons, 10) || 0;
                const takeCartons = ticked ? selection.get(p.id).cartons : origCartons;
                const cartonKg = origCartons > 0 ? parseFloat(p.total_kgs) / origCartons : 0;
                const takeKg = takeCartons * cartonKg;
                const isSplit = ticked && takeCartons < origCartons;

                return (
                  <TableRow key={p.id} sx={{
                    bgcolor: ticked ? (isSplit ? '#fef9c3' : '#fef3c7') : 'transparent',
                    '&:hover': { bgcolor: ticked ? (isSplit ? '#fef9c3' : '#fef3c7') : '#f8fafc' },
                  }}>
                    {selectable && (
                      <TableCell>
                        <Checkbox size="small" sx={{ p: 0 }}
                                  checked={ticked}
                                  onChange={() => onTogglePallet(p)} />
                      </TableCell>
                    )}
                    <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: '#1F618D', fontSize: 12 }}>
                      {p.pallet_id}
                    </TableCell>
                    <TableCell sx={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>{p.production_date || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 11.5 }}>{p.shift || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 11.5, fontWeight: 700 }}>{p.product || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 11.5 }}>{p.grade || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 11.5 }}>{p.pack_style || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 11.5 }}>{p.pack_size || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 11.5 }}>{p.brand || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 11.5, fontFamily: 'JetBrains Mono, monospace', color: '#475569' }}>{p.nav_id || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 11.5, fontFamily: 'JetBrains Mono, monospace', color: '#475569' }}>{p.product_id || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 11.5, fontFamily: 'JetBrains Mono, monospace' }}>{p.lot_no || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 11.5, fontFamily: 'JetBrains Mono, monospace' }}>{p.day_code || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 11.5, fontFamily: 'JetBrains Mono, monospace', color: '#64748b' }}>{p.trace_id || '—'}</TableCell>
                    <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11.5, color: '#64748b', whiteSpace: 'nowrap' }}>
                      {p.cs1 ? `CS1 ${p.cs1}` : '—'}
                      {p.cs2 ? ` · CS2 ${p.cs2}` : ''}
                    </TableCell>
                    <TableCell align="right">
                      {ticked ? (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <input type="number" min="1" max={origCartons}
                               value={takeCartons}
                               onChange={(e) => onSetCartons(p.id, e.target.value)}
                               style={{
                                 width: 60, textAlign: 'right', padding: '3px 5px',
                                 fontFamily: 'JetBrains Mono, monospace',
                                 fontSize: 12.5, fontWeight: 700,
                                 border: '2px solid #f59e0b', borderRadius: 3,
                                 background: '#fffbeb', color: '#92400e',
                               }} />
                        <span style={{ fontSize: 11, color: '#64748b' }}>
                          / {origCartons}
                        </span>
                        {isSplit ? (
                          <Chip label="SPLIT" size="small"
                                sx={{ height: 16, fontSize: 9.5, bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700 }} />
                        ) : (
                          <Chip label="WHOLE" size="small"
                                sx={{ height: 16, fontSize: 9.5, bgcolor: '#dcfce7', color: '#15803d', fontWeight: 700 }} />
                        )}
                      </Box>
                    ) : (
                      <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{origCartons}</span>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11.5 }}>
                    {cartonKg.toFixed(2)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11.5, color: '#64748b' }}>
                    {(cartonKg * 2.20).toFixed(2)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>
                    {isSplit
                      ? <span style={{ color: '#92400e', fontWeight: 700 }}>{takeKg.toFixed(2)}</span>
                      : (parseFloat(p.total_kgs) || 0).toFixed(2)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: '#64748b' }}>
                    {isSplit
                      ? (takeKg * 2.20).toFixed(2)
                      : (parseFloat(p.total_pounds) || 0).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    <Chip label={p.status} size="small"
                          sx={{
                            height: 20, fontSize: 10.5, fontWeight: 700,
                            bgcolor: tab.color + '22', color: tab.color,
                          }} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        </Box>
      </Collapse>
    </Paper>
  );
}
